import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ShieldCheck, ArrowRight, ArrowLeft, Truck, Package, DollarSign,
  MapPin, Phone, Store, FileCheck, Clock, AlertTriangle, Shield,
  CheckCircle2, CreditCard, Zap
} from 'lucide-react';

const TIERS = [
  {
    code: 'A', name: 'Full Shipping', tag: 'per-merchant', price: 'Custom', color: 'from-emerald-500 to-green-500',
    covers: ['Loss', 'Damage'], deductible: null, cap: 'Up to declared value', best: 'Standard commerce'
  },
  {
    code: 'B', name: 'Co-Share', tag: 'per-merchant', price: 'Custom', color: 'from-blue-500 to-cyan-500',
    covers: ['Loss', 'Damage'], deductible: 'Underwritten', cap: 'Underwritten payout', best: 'High-frequency merchants'
  },
  {
    code: 'C', name: 'Chargeback + Shipping', tag: 'per-merchant', price: 'Custom', color: 'from-indigo-500 to-blue-500',
    covers: ['Loss', 'Damage', 'Chargeback'], deductible: null, cap: 'Up to declared value', best: 'Card-not-present sellers'
  },
  {
    code: 'D', name: 'Complete Protection', tag: 'per-merchant', price: 'Custom', color: 'from-purple-500 to-pink-500',
    covers: ['Loss', 'Damage', 'Chargeback', 'Porch piracy', 'Shortage', 'Returns', 'Customs'],
    deductible: null, cap: 'Up to declared value', best: 'Luxury goods · Cross-border'
  },
  {
    code: 'E', name: 'Flat Price', tag: 'flat premium', price: 'Custom', color: 'from-amber-500 to-orange-500',
    covers: ['Loss', 'Damage'], deductible: null, cap: 'High-value flat bands', best: 'Predictable-cost merchants',
  },
];

const SERVICE_DEFAULTS = [
  { name: 'Overnight',   max: 'Underwritten' },
  { name: 'Standard',    max: 'Underwritten' },
  { name: '2-Day',       max: 'Underwritten' },
  { name: '3-Day',       max: 'Underwritten' },
  { name: 'Ground',      max: 'Underwritten' },
  { name: 'Same-Day',    max: 'Underwritten' },
];

const HOW = [
  { icon: Truck,        title: 'Ship as normal',       body: 'Choose your company and get connected through SelleSend. Nothing about your workflow changes.' },
  { icon: ShieldCheck,  title: 'Pick a tier',          body: 'Coverage A through E right next to the rate. One click.' },
  { icon: FileCheck,    title: 'Get a verification #', body: 'Every quote issues a signed SLT-… number with timestamp, ZIP, tier, and SHA-256 evidence hash.' },
  { icon: DollarSign,   title: 'File claims fast',     body: 'Submit the verification number and photos — no long forms.' },
];

const SelleTransLandingPage = () => (
  <div className="min-h-screen bg-background">
    {/* NAV */}
    <nav className="glass sticky top-0 z-50 border-b border-slate-200 dark:border-slate-800">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/selletrans" className="flex items-center gap-3" data-testid="st-logo">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
            <ShieldCheck className="w-7 h-7 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="text-2xl font-bold tracking-tight bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">SelleSend (Parcel Risk)</span>
            <span className="text-[11px] text-purple-600 -mt-0.5">Shipping · Freight · Chargeback Coverage</span>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/"><Button variant="ghost" className="text-slate-500" data-testid="st-back-sellesend"><ArrowLeft className="w-4 h-4 mr-2" />Back to SelleSend</Button></Link>
          <Link to="/login"><Button variant="ghost" className="text-purple-600" data-testid="st-login-btn">Login</Button></Link>
          <Link to="/register">
            <Button className="btn-press bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600" data-testid="st-get-started">
              Get Started <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </nav>

    {/* HERO */}
    <section className="py-20 lg:py-28 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-slate-950 dark:to-purple-950/30">
      <div className="max-w-6xl mx-auto px-6 text-center">
        <p className="text-xs font-mono uppercase tracking-widest text-purple-500 mb-4">COVERAGE FOR EVERY LABEL</p>
        <h1 className="text-5xl md:text-7xl font-semibold tracking-tight mb-6">
          <span className="bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">SelleSend (Parcel Risk)</span>
        </h1>
        <p className="text-xl md:text-3xl text-slate-600 dark:text-slate-300 leading-relaxed mb-8 max-w-3xl mx-auto">
          Insurance built for modern shipping. Five tiers, one platform, every quote comes with a signed verification number.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/register">
            <Button size="lg" className="h-14 px-8 text-lg bg-gradient-to-r from-purple-500 to-indigo-500" data-testid="st-hero-cta">
              Get covered <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <a href="#tiers">
            <Button size="lg" variant="outline" className="h-14 px-8 text-lg border-purple-500 text-purple-600" data-testid="st-hero-tiers">
              Compare tiers
            </Button>
          </a>
        </div>
      </div>
    </section>

    {/* TIERS */}
    <section id="tiers" className="py-20 lg:py-24 bg-white dark:bg-slate-950" data-testid="st-tiers-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-xs font-mono uppercase tracking-widest text-purple-500 mb-3">COVERAGE TIERS</p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Five ways to be covered</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-3xl mx-auto">
            Pick tier by tier. Every SelleSend shipment can be upgraded independently.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {TIERS.map(t => (
            <div key={t.code} className="bg-white dark:bg-slate-900 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800 hover:border-purple-400 hover:shadow-xl transition-all" data-testid={`st-tier-${t.code}`}>
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${t.color} flex items-center justify-center mb-4 shadow-lg`}>
                <span className="text-3xl font-bold text-white">{t.code}</span>
              </div>
              <p className="text-3xl font-bold tabular-nums mb-1">{t.price}</p>
              <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">{t.tag}</p>
              <h3 className="text-lg font-semibold mb-3">{t.name}</h3>
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Covers</p>
              <ul className="mb-3 space-y-1">
                {t.covers.map(c => <li key={c} className="text-sm text-slate-700 dark:text-slate-300 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> {c}</li>)}
              </ul>
              {t.deductible && <p className="text-xs text-amber-700 mb-1"><AlertTriangle className="w-3 h-3 inline mr-1" />Deductible: {t.deductible}</p>}
              <p className="text-xs text-slate-500 mb-3">{t.cap}</p>
              <div className="pt-3 border-t border-slate-200 dark:border-slate-800">
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Best for</p>
                <p className="text-sm font-medium">{t.best}</p>
              </div>
              {t.flat && (
                <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800 space-y-1">
                  {t.flat.map(f => (
                    <div key={f.name} className="flex items-center justify-between text-xs">
                      <span className="font-mono text-slate-500">{f.name}</span>
                      <span className="font-semibold text-slate-400">Custom</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-slate-500 mt-6" data-testid="st-pricing-disclosure">
          Pricing is set on a per-merchant basis after underwriting review. Contact sales for your custom quote.
        </p>
      </div>
    </section>

    {/* SERVICE DEFAULTS */}
    <section className="py-16 bg-slate-50 dark:bg-slate-900" data-testid="st-service-defaults">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-10">
          <p className="text-xs font-mono uppercase tracking-widest text-purple-500 mb-3">SERVICE-LEVEL DEFAULTS</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">Automatic coverage caps by service</h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-3xl mx-auto">Faster services carry higher underwritten coverage automatically. Individual caps set per merchant.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {SERVICE_DEFAULTS.map(s => (
            <div key={s.name} className="bg-white dark:bg-slate-800 rounded-xl p-4 text-center border border-slate-200 dark:border-slate-700" data-testid={`st-svc-${s.name.toLowerCase()}`}>
              <Truck className="w-6 h-6 mx-auto text-purple-500 mb-2" />
              <p className="text-sm font-semibold">{s.name}</p>
              <p className="text-sm font-semibold text-purple-600">{s.max}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          <div className="p-5 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 text-sm">
            <p className="font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-2 mb-2"><MapPin className="w-4 h-4" />High-risk ZIP caps</p>
            <p className="text-amber-800 dark:text-amber-300">Certain high-risk ZIP codes are capped lower and may require an admin-issued call-in override code.</p>
          </div>
          <div className="p-5 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 text-sm">
            <p className="font-semibold text-emerald-800 dark:text-emerald-300 flex items-center gap-2 mb-2"><Clock className="w-4 h-4" />Friday-for-Monday delivery</p>
            <p className="text-emerald-800 dark:text-emerald-300">Ship Friday, deliver Monday — supported and tracked on the verification report.</p>
          </div>
        </div>
      </div>
    </section>

    {/* HOW */}
    <section className="py-20 lg:py-24" data-testid="st-how">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-xs font-mono uppercase tracking-widest text-purple-500 mb-3">HOW IT WORKS</p>
          <h2 className="text-4xl md:text-5xl font-bold">Four steps. Every label. Every time.</h2>
        </div>
        <ol className="space-y-4">
          {HOW.map((h, i) => {
            const Icon = h.icon;
            return (
              <li key={i} className="flex items-start gap-6 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 hover:border-purple-300 dark:hover:border-purple-800" data-testid={`st-step-${i + 1}`}>
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <span className="text-2xl font-bold text-slate-300 dark:text-slate-700 tabular-nums">STEP {i + 1}</span>
                  <h3 className="text-2xl font-semibold mb-1">{h.title}</h3>
                  <p className="text-lg text-slate-600 dark:text-slate-300">{h.body}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>

    {/* VERIFICATION */}
    <section className="py-16 bg-gradient-to-br from-purple-900 to-indigo-900 text-white" data-testid="st-verification">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <Zap className="w-12 h-12 mx-auto mb-4 text-purple-300" />
        <h2 className="text-4xl md:text-5xl font-bold mb-4">Every quote is court-ready</h2>
        <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
          Signed <code className="font-mono px-2 py-1 rounded bg-purple-800/60">SLT-YYYYMMDD-<span className="opacity-70">XXXXXXXXXXXX</span></code> verification number · SHA-256 evidence hash · IP + user-agent + timestamp · FinCEN / SOC-2 auditable.
        </p>
        <Link to="/register">
          <Button size="lg" className="h-14 px-10 text-lg bg-white text-purple-700 hover:bg-purple-50" data-testid="st-final-cta">
            Cover my next shipment <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      </div>
    </section>

    {/* FOOTER */}
    <footer className="py-12 border-t border-slate-200 dark:border-slate-800">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-6 h-6 text-purple-500" />
              <span className="text-xl font-semibold">SelleSend (Parcel Risk)</span>
            </div>
            <p className="text-sm text-slate-500">Coverage for every SelleSend shipment · Powered by the same platform</p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Contact</h4>
            <div className="space-y-2 text-sm text-slate-500">
              <p className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5" /><span>4395 Home Ave · San Diego, CA 92105</span></p>
              <p className="flex items-center gap-2"><Phone className="w-4 h-4" /><a href="tel:6199301074" className="hover:text-primary">(619) 930-1074</a></p>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Related</h4>
            <div className="space-y-1 text-sm text-slate-500">
              <p><Link to="/" className="hover:text-primary">← SelleSend Home</Link></p>
              <p><a href="/legal" className="hover:text-primary">Legal Center</a></p>
              <p><a href="/regulatory" className="hover:text-primary">Regulatory Disclosures</a></p>
              <p><a href="mailto:support@sellesend.com" className="hover:text-primary">Support</a></p>
            </div>
          </div>
        </div>
        <div className="pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2"><Store className="w-4 h-4" />SelleSend (Parcel Risk) © {new Date().getFullYear()}</div>
          <div className="flex flex-wrap gap-4">
            <a href="/privacy"        className="hover:text-primary">Privacy</a>
            <a href="/terms"          className="hover:text-primary">Terms</a>
            <a href="/shipping-terms" className="hover:text-primary">Shipping Terms</a>
            <a href="/aml-compliance" className="hover:text-primary">AML</a>
          </div>
        </div>
      </div>
    </footer>
  </div>
);

export default SelleTransLandingPage;
